import {
  ApiSearchSelect,
  DateSearchSelect,
  InputSearch,
  MultipleSearchSelect
} from '@/components/custom/erp/data-table-v2/search-inputs';
import type { ChiTietGiayBaoCo, GiayBaoCo, GiayBaoCoFilterRow } from '@/types/schemas/giay-bao-co.type';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { formatDate, formatMoney, formatRoundedNumber } from '@/lib/formatUtils';
import { SearchSearchColumnDef } from '@/components/custom/erp';
import type { InputTableColumn } from '@/components/custom/erp';
import { QUERY_KEYS } from '@/constants';

type MasterCellParams = DataTableRenderCellParams<GiayBaoCo | GiayBaoCoFilterRow>;
type DetailCellParams = DataTableRenderCellParams<ChiTietGiayBaoCo>;

const STATUS_OPTIONS = [
  { value: '0', label: 'Lập chứng từ' },
  { value: '3', label: 'Chờ duyệt' },
  { value: '5', label: 'Thu tiền' }
];

const MA_NGV_OPTIONS = [
  { value: '1', label: '1. Theo hoá đơn' },
  { value: '2', label: '2. Theo đối tượng' },
  { value: '3', label: '3. Thu khác' }
];

export const getChangingValueCreditAdviceColumns = (
  handleViewClick: (row: GiayBaoCo | GiayBaoCoFilterRow) => void,
  totalAmount?: number | null
): SearchSearchColumnDef[] => [
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 100,
    renderCell: (params: MasterCellParams) => {
      const status = params.value;
      const option = STATUS_OPTIONS.find(opt => opt.value === status);
      return option?.label || '';
    },
    renderSearchInput: params => <MultipleSearchSelect {...params} options={STATUS_OPTIONS} />
  },
  {
    field: 'so_ct',
    headerName: 'Số c/từ',
    width: 200,
    renderCell: (params: MasterCellParams) => (
      <div
        className='cursor-pointer hover:text-blue-600 hover:underline'
        onClick={e => {
          e.stopPropagation();
          handleViewClick(params.row);
        }}
      >
        {String(params.value ?? '')}
      </div>
    )
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày c/từ',
    width: 130,
    renderCell: (params: MasterCellParams) => formatDate(params.row.ngay_ct),
    renderSearchInput: params => <DateSearchSelect {...params} />
  },
  {
    field: 'ma_kh',
    headerName: 'Mã đối tượng',
    width: 120,
    renderCell: (params: MasterCellParams) =>
      'ma_kh_data' in params.row ? params.row.ma_kh_data?.customer_code || params.row.ma_kh : params.row.ma_kh,
    renderSearchInput: params => (
      <ApiSearchSelect
        {...params}
        apiEndpoint={QUERY_KEYS.AUTOCOMPLETE_DATA}
        model='GIAY_BAO_CO'
        searchField='ma_kh'
        columns={[
          { key: 'customer_code', label: 'Mã khách hàng', width: '120px' },
          { key: 'customer_name', label: 'Tên khách hàng', width: '200px' },
          { key: 'tax_code', label: 'Diễn giải', width: '100px' }
        ]}
        valueKey='customer_code'
        displayKey='customer_code'
      />
    )
  },
  {
    field: 'ten_kh',
    headerName: 'Tên đối tượng',
    width: 200,
    renderCell: (params: MasterCellParams) => {
      const fallbackName = typeof params.row.ten_kh === 'string' ? params.row.ten_kh : '';
      return params.row.ma_kh_data?.customer_name || fallbackName;
    },
    renderSearchInput: params => (
      <ApiSearchSelect
        {...params}
        apiEndpoint={QUERY_KEYS.AUTOCOMPLETE_DATA}
        model='GIAY_BAO_CO'
        searchField='ma_kh'
        columns={[
          { key: 'customer_code', label: 'Mã khách hàng', width: '120px' },
          { key: 'customer_name', label: 'Tên khách hàng', width: '200px' },
          { key: 'tax_code', label: 'Mã thuế', width: '100px' }
        ]}
        valueKey='customer_name'
        displayKey='customer_name'
      />
    )
  },
  { field: 'dien_giai', headerName: 'Diễn giải', width: 200 },
  {
    field: 'sten_tknh',
    headerName: 'Ngân hàng',
    width: 120,
    renderCell: (params: MasterCellParams) =>
      'tknh_data' in params.row ? params.row.tknh_data?.name || params.row.tknh : params.row.tknh
  },
  {
    field: 'tk',
    headerName: 'Tài khoản nợ',
    width: 100,
    renderCell: (params: MasterCellParams) =>
      'tk_data' in params.row ? params.row.tk_data?.code || params.row.tk : params.row.tk,
    renderSearchInput: params => (
      <ApiSearchSelect
        {...params}
        apiEndpoint={QUERY_KEYS.AUTOCOMPLETE_DATA}
        model='GIAY_BAO_CO'
        searchField='tk'
        columns={[
          { key: 'code', label: 'Mã tài khoản', width: '120px' },
          { key: 'name', label: 'Tên tài khoản', width: '200px' }
        ]}
        valueKey='code'
        displayKey='code'
      />
    )
  },
  {
    field: 't_tien_nt',
    headerName: 'Tổng tiền',
    width: 160,
    type: 'number',
    align: 'right',
    renderCell: (params: MasterCellParams) => formatRoundedNumber(params.row.t_tien_nt ?? 0),
    renderSearchInput: params => (
      <InputSearch
        {...params}
        onKeyDown={params.onKeyDown ?? (() => {})}
        suffixContent={
          <div className='flex items-center gap-2'>
            <span className='text-lg font-bold'>&#931;</span>
            <span className='font-semibold text-main'>
              {typeof totalAmount === 'number' ? formatRoundedNumber(totalAmount) : '—'}
            </span>
          </div>
        }
      />
    )
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 100,
    renderCell: (params: MasterCellParams) =>
      'ma_nt_data' in params.row ? params.row.ma_nt_data?.ma_nt || params.row.ma_nt : params.row.ma_nt
  },
  {
    field: 'ma_ngv',
    headerName: 'Loại chứng từ',
    width: 120,
    renderCell: (params: MasterCellParams) => {
      const status = params.value;
      const option = MA_NGV_OPTIONS.find(opt => opt.value === status);
      return option?.label || '';
    },
    renderSearchInput: params => <MultipleSearchSelect {...params} options={MA_NGV_OPTIONS} />
  }
];

export const exportCreditAdviceDetailColumns: InputTableColumn[] = [
  {
    field: 'ma_kh',
    headerName: 'Mã đối tượng',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_kh_data?.customer_code || ''
  },
  {
    field: 'ten_kh',
    headerName: 'Tên đối tượng',
    width: 200,
    renderCell: (params: DetailCellParams) => params.row.ma_kh_data?.customer_name || ''
  },
  {
    field: 'du_cn',
    headerName: 'Dư công nợ',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_kh_data?.credit_limit || ''
  },
  {
    field: 'id_hd',
    headerName: 'Hoá đơn',
    width: 180,
    renderCell: (params: DetailCellParams) => params.row.id_hd_data?.so_ct || ''
  },
  {
    field: 'ngay_ct_hd',
    headerName: 'Ngày hoá đơn',
    width: 120,
    renderCell: (params: DetailCellParams) => formatDate(params.row.id_hd_data?.ngay_ct) || ''
  },
  {
    field: 'tk_co',
    headerName: 'Tài khoản có',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.tk_co_data?.code || params.row.tk_co || ''
  },
  {
    field: 'ten_tk_co',
    headerName: 'Tên tài khoản',
    width: 150,
    renderCell: (params: DetailCellParams) => params.row.tk_co_data?.name || ''
  },
  {
    field: 'ma_nt_hd',
    headerName: 'Ngoại tệ',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.id_hd_data?.ngoai_te || ''
  },
  {
    field: 'ty_gia_hd',
    headerName: 'Tỷ giá hđ',
    width: 100,
    type: 'number',
    align: 'right',
    renderCell: (params: DetailCellParams) => params.row.id_hd_data?.ty_gia || 0
  },
  {
    field: 'tien_hd_nt',
    headerName: 'Tiền trên hoá đơn',
    type: 'number',
    align: 'right',
    width: 150,
    renderCell: (params: DetailCellParams) => formatMoney(params.row.id_hd_data?.tien_tren_hd ?? 0)
  },
  {
    field: 'da_pb_nt',
    headerName: 'Đã phân bổ',
    type: 'number',
    width: 120,
    renderCell: (params: DetailCellParams) => Number(params.row.id_hd_data?.da_thanh_toan ?? params.row.da_pb_nt ?? 0)
  },
  {
    field: 'cl_nt',
    headerName: 'Còn lại',
    type: 'number',
    align: 'right',
    width: 120,
    renderCell: (params: DetailCellParams) => formatMoney(params.row.id_hd_data?.tien_con_phai_tt ?? 0)
  },
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    type: 'number',
    align: 'right',
    width: 140,
    renderCell: (params: DetailCellParams) => formatMoney(params.row.tien_nt || 0)
  },
  { field: 'dien_giai', headerName: 'Diễn giải', width: 250 },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 200,
    renderCell: (params: DetailCellParams) => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 150,
    renderCell: (params: DetailCellParams) => params.row.ma_vv_data?.ma_vu_viec || params.row.ma_vv
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 150,
    renderCell: (params: DetailCellParams) => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 150,
    renderCell: (params: DetailCellParams) => params.row.ma_dtt_data?.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 150,
    renderCell: (params: DetailCellParams) => params.row.ma_ku_data?.ma_ku
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 150,
    renderCell: (params: DetailCellParams) => params.row.ma_phi_data?.ma_phi
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 150,
    renderCell: (params: DetailCellParams) => params.row.ma_sp_data?.ma_vt
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 150,
    renderCell: (params: DetailCellParams) => params.row.ma_lsx_data?.so_ct
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: (params: DetailCellParams) => params.row.ma_cp0_data?.ma_cp_khl
  }
];
